import { useState, useEffect } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleModalOpen,
  setValidationErrors,
  clearValidationErrors,
  resetScheduleForm,
  setScheduleCreated,
  clearEditingSchedule,
} from "../../../store/schedulingReducer";
import { setStudentsList } from "../../../store/studentManagementReducer";
import SelectStudent from "./sections/SelectStudent";
import ScheduleTimeFields from "./sections/ScheduleTimeFields";
import useScheduleValidation from "./useScheduleValidation";
import { scheduleInterview } from "../../../application/services/scheduleTabService";
import "../Style.css";
import SelectInteview from "./sections/SelectInterview";
import { updateScheduleInterview } from "../../../application/services/scheduleTabService";
import { updateFormField } from "../../../store/schedulingReducer";

const ScheduleInterviewModal = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { validationErrors, isModalOpen } = useSelector((state) => state.scheduling);
  const schedulingData = useSelector((state) => state.scheduling);

  useEffect(() => {
    if (isModalOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen && !schedulingData.editingScheduleId) {
      dispatch(clearEditingSchedule());
      dispatch(resetScheduleForm());
    }
  }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onclose = () => {
    dispatch(clearValidationErrors());
    dispatch(resetScheduleForm());
    dispatch(setStudentsList([]));
    dispatch(toggleModalOpen(false));
    dispatch(clearEditingSchedule());
  };

  const {
    formErrors,
    isScheduleDisabled,
    setFormErrors,
    validateForm,
    preparePayload,
  } = useScheduleValidation(schedulingData);

  const onSchedule = async (e) => {
    e.preventDefault();
    try {
      dispatch(clearValidationErrors());
      setFormErrors({});

      if (!validateForm()) {
        dispatch(setValidationErrors(formErrors));
        return;
      }

      setIsSubmitting(true);

      const payload = preparePayload();
      if (schedulingData.editingScheduleId) {
        await updateScheduleInterview(
          schedulingData.editingScheduleId,
          payload
        );
      } else {
       await scheduleInterview(payload);
      }

      setFormErrors({});
      dispatch(resetScheduleForm());
      dispatch(setStudentsList([]));
      dispatch(setScheduleCreated(true));
      dispatch(toggleModalOpen(false));
    } catch (error) {
      console.error("Error scheduling interview:", error);

      if (error.data?.errors) {
        dispatch(setValidationErrors(error.data.errors));
      } else {
        const errorMsg =
          error.data?.message ||
          "Failed to schedule interview. Please try again.";
        console.log(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (schedulingData.editingScheduleId && schedulingData.initialFormValues && schedulingData.isModalOpen) {
      const { form, studentName, studentEmail, studentId, interviewType, interviewTypeId } =
        schedulingData.initialFormValues;
      if (studentName !== undefined) {
        dispatch(updateFormField({ key: "studentName", value: studentName || "" }));
      }
      if (studentEmail !== undefined) {
        dispatch(updateFormField({ key: "studentEmail", value: studentEmail || "" }));
      }
      if (studentId !== undefined) {
        dispatch(updateFormField({ key: "studentId", value: studentId || "" }));
      }
      if (interviewType !== undefined) {
        dispatch(updateFormField({ key: "interviewType", value: interviewType || "" }));
      }
      if (interviewTypeId !== undefined) {
        dispatch(updateFormField({ key: "interviewTypeId", value: interviewTypeId || "" }));
      }
      if (form) {
        Object.entries(form).forEach(([key, value]) => {
          if (value !== undefined) {
            dispatch(updateFormField({ key, value: value || "" }));
          }
        });
      }
    }
  }, [schedulingData.editingScheduleId, schedulingData.initialFormValues, schedulingData.isModalOpen, dispatch]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="space-y-6 font-sans">
      <div className="modal-overlay p-4">
        <div className="modal-container">
          <div className="modal-header">
            <h2 className="text-xl font-bold text-gray-900">
              Schedule Interview
            </h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={onclose}
              disabled={isSubmitting}
            >
              {SvgMapper.closeIcon("w-6 h-6")}
            </button>
          </div>
          <form className="p-6 space-y-8">
            {hasErrors && (
              <div className="error-box">
                <ul className="error-list">
                  {Object.entries(validationErrors || {}).map(
                    ([field, error]) => (
                      <li key={field}>{error}</li>
                    )
                  )}
                </ul>
              </div>
            )}

            <SelectStudent />
            <SelectInteview />

            <ScheduleTimeFields />
            <div className="pt-4 pb-6 px-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onclose}
                className="cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isScheduleDisabled }
                className={`submit-btn ${
                  isSubmitting || isScheduleDisabled ? "disabled" : ""
                }`}
                onClick={onSchedule}
              >
                {SvgMapper.saveIcon("w-4 h-4")}
                <span>
                  {isSubmitting ? "Scheduling..." : "Schedule Interview"}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;